import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HelpPageSectionDTO, HelpSectionZone } from '@/domain/repositories/IHelpDocumentationRepository';

const LOCALES = ['es', 'en', 'pt'] as const;
const ZONES: HelpSectionZone[] = ['top_main', 'top_aside', 'mid_main', 'mid_aside', 'bottom'];

type PreviewLocale = (typeof LOCALES)[number];
type Selection = { kind: 'section'; id: string } | null;

function isRetiredSection(section: HelpPageSectionDTO): boolean {
  return (
    section.sectionKey === 'quick_actions' ||
    section.sectionKey === 'faq' ||
    section.sectionKey === 'tips' ||
    section.sectionKey === 'announcements'
  );
}

function moveItem<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const tmp = next[index];
  next[index] = next[target];
  next[target] = tmp;
  return next;
}

function titleFor(section: HelpPageSectionDTO, locale: PreviewLocale, fallback: string): string {
  return (
    section.locales.find((l) => l.locale === locale)?.title ||
    section.locales.find((l) => l.locale === 'es')?.title ||
    fallback
  );
}

const SECTION_SKETCH: Record<string, { accent: string; sketch: string }> = {
  documentation: {
    accent: 'border-sky-300 bg-sky-50/80 dark:border-sky-700 dark:bg-sky-900/20',
    sketch: 'list',
  },
  human_support: {
    accent: 'border-violet-300 bg-violet-50/80 dark:border-violet-700 dark:bg-violet-900/20',
    sketch: 'card',
  },
  faq: {
    accent: 'border-amber-300 bg-amber-50/80 dark:border-amber-700 dark:bg-amber-900/20',
    sketch: 'accordion',
  },
  tips: {
    accent: 'border-lime-300 bg-lime-50/80 dark:border-lime-700 dark:bg-lime-900/20',
    sketch: 'cards',
  },
  announcements: {
    accent: 'border-rose-300 bg-rose-50/80 dark:border-rose-700 dark:bg-rose-900/20',
    sketch: 'banner',
  },
};

function Sketch({ kind }: { kind: string }) {
  if (kind === 'list') {
    return (
      <div className="mt-2 space-y-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-4 rounded bg-white/70 dark:bg-gray-800/60 border border-black/5" />
        ))}
      </div>
    );
  }
  if (kind === 'card') {
    return (
      <div className="mt-2 space-y-1.5">
        <div className="h-6 rounded bg-teal-600/80" />
        <div className="h-6 rounded border border-black/10 bg-white/70 dark:bg-gray-800/60" />
      </div>
    );
  }
  if (kind === 'accordion') {
    return (
      <div className="mt-2 space-y-1">
        {[1, 2].map((i) => (
          <div key={i} className="h-5 rounded bg-white/70 dark:bg-gray-800/60 border border-black/5 flex items-center px-2">
            <span className="w-2 h-2 rounded-full bg-gray-300 mr-2" />
            <span className="h-1.5 flex-1 rounded bg-gray-200 dark:bg-gray-600" />
          </div>
        ))}
      </div>
    );
  }
  if (kind === 'cards') {
    return (
      <div className="mt-2 space-y-1">
        {[1, 2].map((i) => (
          <div key={i} className="h-8 rounded bg-white/70 dark:bg-gray-800/60 border border-black/5" />
        ))}
      </div>
    );
  }
  return (
    <div className="mt-2 grid grid-cols-2 gap-1">
      <div className="h-10 rounded bg-white/70 dark:bg-gray-800/60 border border-black/5" />
      <div className="h-10 rounded bg-white/70 dark:bg-gray-800/60 border border-black/5" />
    </div>
  );
}

export interface HelpLayoutEditorProps {
  sections: HelpPageSectionDTO[];
  onSectionsChange: (next: HelpPageSectionDTO[]) => void;
  onSave: () => void;
  saving: boolean;
  loading?: boolean;
  error?: string | null;
}

export const HelpLayoutEditor: React.FC<HelpLayoutEditorProps> = ({
  sections,
  onSectionsChange,
  onSave,
  saving,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const [previewLocale, setPreviewLocale] = useState<PreviewLocale>('es');
  const [selection, setSelection] = useState<Selection>(null);

  const sectionsByZone = useMemo(() => {
    const map = new Map<HelpSectionZone, HelpPageSectionDTO[]>();
    for (const zone of ZONES) map.set(zone, []);
    for (const section of sections) {
      if (!section.isVisible || isRetiredSection(section)) continue;
      map.get(section.zone)?.push(section);
    }
    for (const zone of ZONES) {
      map.get(zone)?.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return map;
  }, [sections]);

  const hiddenSections = useMemo(
    () =>
      sections
        .filter((s) => !s.isVisible && !isRetiredSection(s))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [sections]
  );

  const selectedSection =
    selection?.kind === 'section'
      ? sections.find((s) => s.id === selection.id && !isRetiredSection(s)) ?? null
      : null;

  useEffect(() => {
    if (
      selection?.kind === 'section' &&
      selection.id &&
      sections.some((s) => s.id === selection.id && !isRetiredSection(s))
    ) {
      return;
    }
    const first =
      sections.find((s) => s.isVisible && !isRetiredSection(s)) ??
      sections.find((s) => !isRetiredSection(s));
    if (first) setSelection({ kind: 'section', id: first.id });
  }, [sections, selection]);

  const updateSection = (id: string, patch: Partial<HelpPageSectionDTO>) => {
    onSectionsChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const setSectionTitle = (id: string, locale: string, title: string) => {
    onSectionsChange(
      sections.map((s) => {
        if (s.id !== id) return s;
        const locales = [...s.locales];
        const idx = locales.findIndex((l) => l.locale === locale);
        if (idx >= 0) locales[idx] = { ...locales[idx], title };
        else locales.push({ locale, title });
        return { ...s, locales };
      })
    );
  };

  const moveSectionToZone = (id: string, zone: HelpSectionZone) => {
    const inTarget = sections.filter(
      (s) => s.zone === zone && s.id !== id && s.isVisible && !isRetiredSection(s)
    );
    onSectionsChange(
      sections.map((s) =>
        s.id === id ? { ...s, zone, sortOrder: inTarget.length + 1, isVisible: true } : s
      )
    );
  };

  const reorderInZone = (zone: HelpSectionZone, index: number, direction: -1 | 1) => {
    const list = sectionsByZone.get(zone) ?? [];
    const next = moveItem(list, index, direction);
    if (next === list) return;
    const reordered = next.map((s, i) => ({ ...s, sortOrder: i + 1 }));
    onSectionsChange(
      sections.map((s) => {
        const updated = reordered.find((r) => r.id === s.id);
        return updated ?? s;
      })
    );
  };

  const renderPreviewBlock = (section: HelpPageSectionDTO, index: number, zone: HelpSectionZone) => {
    const selected = selection?.kind === 'section' && selection.id === section.id;
    const meta = SECTION_SKETCH[section.sectionKey] ?? SECTION_SKETCH.documentation;
    const name = t(`pages.documentation.sectionKeys.${section.sectionKey}`);
    const title = titleFor(section, previewLocale, name);
    const siblings = sectionsByZone.get(zone) ?? [];

    return (
      <button
        key={section.id}
        type="button"
        onClick={() => setSelection({ kind: 'section', id: section.id })}
        className={`w-full text-left rounded-xl border-2 p-3 transition-all ${meta.accent} ${
          selected
            ? 'ring-2 ring-teal-500 border-teal-500 shadow-md'
            : 'hover:shadow-sm border-transparent'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {name}
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {title}
            </div>
          </div>
          <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="px-1.5 text-xs rounded border border-black/10 bg-white/80 disabled:opacity-30"
              disabled={index === 0}
              onClick={() => reorderInZone(zone, index, -1)}
              title={t('pages.documentation.moveUp')}
            >
              ↑
            </button>
            <button
              type="button"
              className="px-1.5 text-xs rounded border border-black/10 bg-white/80 disabled:opacity-30"
              disabled={index === siblings.length - 1}
              onClick={() => reorderInZone(zone, index, 1)}
              title={t('pages.documentation.moveDown')}
            >
              ↓
            </button>
          </div>
        </div>
        <Sketch kind={meta.sketch} />
      </button>
    );
  };

  if (loading) {
    return <p className="text-sm text-gray-500">{t('pages.documentation.loading')}</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('pages.documentation.layoutBuilderTitle')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('pages.documentation.layoutBuilderHint')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
            {LOCALES.map((locale) => (
              <button
                key={locale}
                type="button"
                onClick={() => setPreviewLocale(locale)}
                className={`px-3 py-1.5 text-xs font-medium ${
                  previewLocale === locale
                    ? 'bg-teal-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                }`}
              >
                {locale.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className="btn btn-primary"
          >
            {saving ? t('pages.documentation.saving') : t('pages.documentation.saveLayout')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.9fr)] gap-4 items-start">
        {/* Preview */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('pages.documentation.previewLabel')}
            </div>
            <div className="text-[11px] text-gray-400">
              {t('pages.documentation.previewClickHint')}
            </div>
          </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 text-center">
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {t('pages.documentation.previewSearchLead')}
              </div>
              <div className="mt-2 mx-auto max-w-md h-9 rounded-lg border border-gray-200 dark:border-gray-600 bg-slate-50 dark:bg-gray-900/50" />
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
                <div className="space-y-3">
                  {(sectionsByZone.get('top_main') ?? []).map((s, i) =>
                    renderPreviewBlock(s, i, 'top_main')
                  )}
                  {(sectionsByZone.get('top_main') ?? []).length === 0 && (
                    <EmptyZone
                      label={t('pages.documentation.zones.top_main')}
                      onDropZone={() => selectedSection && moveSectionToZone(selectedSection.id, 'top_main')}
                    />
                  )}
                </div>
                <div className="space-y-3">
                  {(sectionsByZone.get('top_aside') ?? []).map((s, i) =>
                    renderPreviewBlock(s, i, 'top_aside')
                  )}
                  <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-[11px] font-medium text-green-700 dark:text-green-400">
                      {t('pages.documentation.previewStatus')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
                <div className="space-y-3">
                  {(sectionsByZone.get('mid_main') ?? []).map((s, i) =>
                    renderPreviewBlock(s, i, 'mid_main')
                  )}
                </div>
                <div className="space-y-3">
                  {(sectionsByZone.get('mid_aside') ?? []).map((s, i) =>
                    renderPreviewBlock(s, i, 'mid_aside')
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {(sectionsByZone.get('bottom') ?? []).map((s, i) =>
                  renderPreviewBlock(s, i, 'bottom')
                )}
              </div>
            </div>
          </div>

          {hiddenSections.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                {t('pages.documentation.hiddenBlocks')}
              </div>
              <div className="flex flex-wrap gap-2">
                {hiddenSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setSelection({ kind: 'section', id: section.id })}
                    className={`px-3 py-1.5 rounded-full text-xs border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 ${
                      selection?.kind === 'section' && selection.id === section.id
                        ? 'ring-2 ring-teal-500'
                        : ''
                    }`}
                  >
                    {t(`pages.documentation.sectionKeys.${section.sectionKey}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Inspector */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('pages.documentation.inspectorTitle')}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {selectedSection
                ? t(`pages.documentation.sectionKeys.${selectedSection.sectionKey}`)
                : t('pages.documentation.inspectorEmpty')}
            </div>
          </div>

          {selectedSection ? (
            <div className="p-4 space-y-5">
              <label className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-200">
                <span>{t('pages.documentation.visible')}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={selectedSection.isVisible}
                  onClick={() =>
                    updateSection(selectedSection.id, { isVisible: !selectedSection.isVisible })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    selectedSection.isVisible ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      selectedSection.isVisible ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>

              <div>
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  {t('pages.documentation.placement')}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      ['top_main', 'top_aside'],
                      ['mid_main', 'mid_aside'],
                    ] as const
                  ).map((row) => (
                    <React.Fragment key={row[0]}>
                      {row.map((zone) => (
                        <button
                          key={zone}
                          type="button"
                          onClick={() => moveSectionToZone(selectedSection.id, zone)}
                          className={`rounded-lg border px-2 py-3 text-[11px] text-left transition ${
                            selectedSection.zone === zone
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-200'
                              : 'border-gray-200 dark:border-gray-600 hover:border-teal-300 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {t(`pages.documentation.zonesShort.${zone}`)}
                        </button>
                      ))}
                    </React.Fragment>
                  ))}
                  <button
                    type="button"
                    onClick={() => moveSectionToZone(selectedSection.id, 'bottom')}
                    className={`col-span-2 rounded-lg border px-2 py-3 text-[11px] text-left transition ${
                      selectedSection.zone === 'bottom'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-200'
                        : 'border-gray-200 dark:border-gray-600 hover:border-teal-300 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {t('pages.documentation.zonesShort.bottom')}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500">
                  {t('pages.documentation.titlesByLocale')}
                </div>
                {LOCALES.map((locale) => (
                  <label key={locale} className="block text-xs text-gray-500">
                    {locale.toUpperCase()}
                    <input
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      value={selectedSection.locales.find((l) => l.locale === locale)?.title ?? ''}
                      onChange={(e) => setSectionTitle(selectedSection.id, locale, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-6 text-sm text-gray-500">
              {t('pages.documentation.inspectorEmpty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function EmptyZone({
  label,
  onDropZone,
}: {
  label: string;
  onDropZone: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onDropZone}
      className="w-full rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-3 py-6 text-xs text-gray-400 hover:border-teal-400 hover:text-teal-500"
    >
      {label}
    </button>
  );
}
